import { Modal } from '@tempmee/react';
import { type CSSProperties, useLayoutEffect, useRef } from 'react';
import { MODAL_OVERLAY_Z_INDEX } from '~/constants/layout';
import { ConfirmPopupContent, type IConfirmPopupContentProps } from './ConfirmPopupContent';

/**
 * DS `Modal` portals a root `<div class="fixed inset-0 z-50">`. This app's
 * stacking context places the sidebar / office-switcher and several popups at
 * z-index 1000–9999, so a `z-50` modal renders *behind* them. The legacy
 * ConfirmPopup forced `z-index: 9999`; we restore that on the portal root.
 */

export interface IConfirmPopupProps extends IConfirmPopupContentProps {
  /**
   * Controls visibility.
   *
   * Defaults to `true` so existing conditional-mount call sites
   * (`{flag && <ConfirmPopup .../>}`) behave exactly as before: mounting the
   * component shows it. New call sites may keep the modal permanently mounted
   * and drive `open` instead, which unlocks the DS `Modal` enter/exit
   * animation and focus management.
   */
  open?: boolean;
  /** `data-cy` for the modal panel. Defaults to `"modal"` (legacy selector). */
  dataCy?: string;
  /** `data-testid` forwarded to the modal panel. */
  dataTestId?: string;
  /** Inline style for the modal panel (width / height / padding overrides). */
  modalStyle?: CSSProperties;
  /**
   * @deprecated The design-system `Modal` owns the backdrop, so this no longer
   * has an effect. Retained only so existing call sites keep compiling; safe to
   * remove from callers. The single historical use (`{ zIndex: 'auto' }`) was a
   * workaround for the legacy `z-index: 9999` that DS `Modal` (`z-50`) makes moot.
   */
  modalContainerStyle?: CSSProperties;
  /** When `false`, backdrop click / Escape will not dismiss. Defaults to `true`. */
  dismissiblePopup?: boolean;
  /** Dismiss handler — wired to backdrop click, Escape, and the close (X) icon. */
  closePopup?: () => void;
}

const noop = (): void => {};

/**
 * ConfirmPopup
 *
 * Shared modal shell for the dental portal. Historically it rendered its own
 * `Portal` + `.modal` backdrop + `framer-motion` content; it now delegates the
 * chrome (portal, backdrop, focus-trap, scroll-lock, Escape/overlay dismiss) to
 * the design-system `Modal` while preserving the public prop API.
 *
 * Selector parity: the panel keeps `data-cy="modal"` and the `.modal_content`
 * class (used by E2E and for flex centering). The close icon keeps
 * `data-cy="modal-close"` and the CTA keeps `data-testid="right-button"`.
 *
 * NOTE: the legacy backdrop selector `data-cy="modal-backdrop"` no longer
 * exists — DS `Modal` renders the backdrop itself with a hardcoded
 * `data-testid="modal-overlay"`. E2E helpers that dismissed via
 * `[data-cy="modal-backdrop"]` must target `[data-testid="modal-overlay"]`
 * (or click the close button).
 *
 * @example
 * ```tsx
 * {showConfirm && (
 *   <ConfirmPopup
 *     title="Delete draft?"
 *     description="This cannot be undone."
 *     leftButtonText="Cancel"
 *     leftButtonAction={() => setShowConfirm(false)}
 *     rightButtonText="Delete"
 *     rightButtonAction={handleDelete}
 *     closePopup={() => setShowConfirm(false)}
 *     showClosePopupIcon
 *   />
 * )}
 * ```
 */
export default function ConfirmPopup({
  open = true,
  dataCy,
  dataTestId,
  modalStyle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modalContainerStyle: _modalContainerStyle,
  dismissiblePopup = true,
  closePopup,
  closePopupDataCy,
  ...contentProps
}: IConfirmPopupProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Raise the DS Modal's portal root above the app's high z-index elements
  // (sidebar/dropdowns at 1000, ad-hoc popups at 9999). DS Modal only spreads
  // props to the panel, so we walk up to the body-level portal root and set it
  // directly. useLayoutEffect runs before paint, so there is no z-50 flash.
  useLayoutEffect(() => {
    if (!open) return;
    let node: HTMLElement | null = panelRef.current;
    while (node?.parentElement && node.parentElement !== document.body) {
      node = node.parentElement;
    }
    if (node) node.style.zIndex = String(MODAL_OVERLAY_Z_INDEX);
  }, [open]);

  return (
    <Modal
      ref={panelRef}
      open={open}
      onClose={closePopup ?? noop}
      closeOnOverlayClick={dismissiblePopup}
      closeOnEscape={dismissiblePopup}
      // `outline-none` suppresses the browser's default focus ring on the panel.
      // DS Modal calls panel.focus() on open, making it document.activeElement.
      // The panel's `focus-visible:outline-none` Tailwind class only fires when
      // :focus-visible is true (keyboard nav); programmatic focus leaves no plain
      // `outline-none`, so the browser's default blue ring bleeds through.
      className="modal_content outline-none"
      data-cy={dataCy || 'modal'}
      data-testid={dataTestId}
      // The DS panel is `w-full max-w-md`; neutralize both so legacy widths
      // (500px default, or whatever `modalStyle` supplies) are honored exactly.
      style={{ width: 500, maxWidth: 'none', padding: '45px 10px', ...modalStyle }}
    >
      <ConfirmPopupContent
        {...contentProps}
        closePopup={closePopup}
        closePopupDataCy={closePopupDataCy || 'modal-close'}
      />
    </Modal>
  );
}
